import { CatalogPriceChangeRow } from '../services/integrationApi';
import {
  canGenerateCanonicalProductId,
  generateCanonicalProductId,
} from './canonicalProductId';

/** Stable identity of a change row: report doc id + index in its changes array. */
export function catalogPriceChangeRowKey(row: CatalogPriceChangeRow): string {
  return `${row.reportId}:${row.changeIndex}`;
}

export function resolveCatalogPriceChangeProductId(row: CatalogPriceChangeRow): string | null {
  if (row.productId) return row.productId;
  if (row.brandId && row.sku && canGenerateCanonicalProductId(row.brandId, row.sku)) {
    return generateCanonicalProductId(row.brandId, row.sku);
  }
  return null;
}

/**
 * Rows where the price did not actually move (e.g. a flagged discontinuation,
 * or legacy fabricated entries). These are not price changes and get
 * presented — and actioned — differently.
 */
export function isFlatPriceRow(row: CatalogPriceChangeRow): boolean {
  return row.oldPrice === row.newPrice;
}

/** Displayed prices may include promotions; only an explicit retail move is actionable. */
export function retailRevertChange(row: CatalogPriceChangeRow): { oldPrice: number; newPrice: number } | null {
  const change = row.rawPriceChanges?.find((entry) => entry.field === 'retail');
  if (!change || change.oldPriceIsFallback || !Number.isFinite(change.oldPrice) || change.oldPrice <= 0
    || !Number.isFinite(change.newPrice) || change.newPrice <= 0 || change.oldPrice === change.newPrice) return null;
  return { oldPrice: change.oldPrice, newPrice: change.newPrice };
}

/** Deny/revert writes retail, never a displayed, sale or compare-at price. */
export function canRevertApplied(row: CatalogPriceChangeRow): boolean {
  return (
    !row.resolved &&
    (row.action === 'applied_needs_review' || row.action === 'fast_tracked') &&
    row.field !== 'sale' &&
    retailRevertChange(row) !== null
  );
}

/** A deviation guard held this read. The page uses it to offer approve/reject instead of the generic apply. */
export function isHeldPriceMove(row: Pick<CatalogPriceChangeRow, 'priceMoveReview'>): boolean {
  const review = row.priceMoveReview;
  return Boolean(review && review.reason.trim() && review.sku.trim());
}

/** Scrape was blocked — apply forces newPrice onto the live product retail. */
export function canApplyBlocked(row: CatalogPriceChangeRow): boolean {
  return (
    !row.resolved &&
    row.action === 'rejected_suspicious' &&
    Number.isFinite(row.newPrice) &&
    row.newPrice > 0 &&
    row.field !== 'sale'
  );
}

function sameCatalogPriceChangeRegion(a: CatalogPriceChangeRow, b: CatalogPriceChangeRow): boolean {
  return (a.region || 'US').toUpperCase() === (b.region || 'US').toUpperCase();
}

/** Same live product. Product id wins; brand+SKU is only a fallback when an id is missing. */
export function isSameCatalogPriceChangeProduct(
  acted: CatalogPriceChangeRow,
  row: CatalogPriceChangeRow
): boolean {
  if (!sameCatalogPriceChangeRegion(acted, row)) return false;
  const actedId = resolveCatalogPriceChangeProductId(acted);
  const rowId = resolveCatalogPriceChangeProductId(row);
  if (actedId && rowId) return actedId === rowId;
  return Boolean(acted.sku && row.sku === acted.sku && row.brandId === acted.brandId);
}

/**
 * After an admin applies/denies/reverts a live price, clear that review row
 * and any other unresolved reports for the same product + field (e.g. the
 * same SKU from a later scrape). Otherwise the table still shows
 * "Blocked (suspicious)" because reports are historical snapshots.
 */
export function rowsToResolveAfterPriceAction(
  acted: CatalogPriceChangeRow,
  all: CatalogPriceChangeRow[]
): Array<{ reportId: string; changeIndex: number }> {
  const seen = new Set<string>();
  const items: Array<{ reportId: string; changeIndex: number }> = [];
  for (const row of all) {
    if (row.resolved) continue;
    if ((row.field || 'retail') !== (acted.field || 'retail')) continue;
    if (!isSameCatalogPriceChangeProduct(acted, row)) continue;
    const key = catalogPriceChangeRowKey(row);
    if (seen.has(key)) continue;
    seen.add(key);
    items.push({ reportId: row.reportId, changeIndex: row.changeIndex });
  }
  if (items.length === 0) {
    items.push({ reportId: acted.reportId, changeIndex: acted.changeIndex });
  }
  return items;
}

/**
 * Group key for collapsing repeat scrape blocks of the same live price
 * change into one review row (product + region + field + action).
 * Resolved vs unresolved stay separate so "Show resolved" does not merge
 * an open block with a closed one.
 */
export function catalogPriceChangeGroupKey(row: CatalogPriceChangeRow): string {
  const productId = resolveCatalogPriceChangeProductId(row);
  const product =
    productId ||
    `${String(row.brandId || '').toLowerCase()}|${String(row.sku || '').toUpperCase()}`;
  const region = (row.region || 'US').toUpperCase();
  const field = row.field || 'retail';
  const action = row.action || '';
  const resolved = row.resolved ? '1' : '0';
  return `${product}|${region}|${field}|${action}|${resolved}`;
}

export interface CatalogPriceChangeGroup {
  /** Newest report for this product/action — what the table displays. */
  primary: CatalogPriceChangeRow;
  /** All matching reports, newest first. */
  members: CatalogPriceChangeRow[];
  /** How many scrapes logged this same block/change. */
  attemptCount: number;
}

/**
 * Collapse historical pricingReports that re-blocked (or re-applied) the
 * same product into one reviewable group. Order follows the input order of
 * first appearance after members are sorted newest-first within each group.
 */
export function groupCatalogPriceChangeRows(
  rows: CatalogPriceChangeRow[]
): CatalogPriceChangeGroup[] {
  const buckets = new Map<string, CatalogPriceChangeRow[]>();
  const order: string[] = [];
  for (const row of rows) {
    const key = catalogPriceChangeGroupKey(row);
    let list = buckets.get(key);
    if (!list) {
      list = [];
      buckets.set(key, list);
      order.push(key);
    }
    list.push(row);
  }
  return order.map((key) => {
    const members = [...(buckets.get(key) || [])].sort((a, b) =>
      (b.reportedAt || '').localeCompare(a.reportedAt || '')
    );
    return {
      primary: members[0],
      // A single scrape job can log duplicate SKU/field entries in one
      // report, so attempts are distinct report ids, not raw member count.
      members,
      attemptCount: new Set(members.map((m) => m.reportId)).size,
    };
  });
}
